/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { StatusBadge } from 'src/components/common/StatusBadge';
import { useParams, useSearchParams } from 'react-router-dom';
import { Mail, Phone, Calendar, Users, UserRoundPlus } from 'lucide-react';
import DataTable from 'src/components/ui/data-table';
import { Switch } from 'src/components/ui/switch';
import { useToast } from 'src/hooks/use-toast';
import { PartnerService } from 'src/services/api/PartnerService';
import { useAddPartnerMember } from 'src/services/api/mutations/partnerMutations';
import { AddPartnerMemberModal } from 'src/components/Modals/AddPartnerMemberModal';

const DEFAULT_PAGE_SIZE = 10;

const getInitials = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

const NameCell = ({ row }) => {
  const name = [
    row.original.first_name,
    row.original.middle_name,
    row.original.last_name,
  ]
    .filter(Boolean)
    .join(' ');
  return <span className="text-[13px] font-[550] text-gray-800">{name}</span>;
};

const EmailCell = ({ value }) => (
  <div className="flex items-center gap-1.5">
    <Mail
      className="w-3.5 h-3.5 text-primary flex-shrink-0"
      strokeWidth={1.8}
    />
    <span className="text-[12.5px] text-[#2168B2] truncate">{value}</span>
  </div>
);

const PhoneCell = ({ value }) => {
  if (!value) return <span className="text-[12.5px] text-gray-400">—</span>;
  return (
    <div className="flex items-center gap-1.5">
      <Phone
        className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
        strokeWidth={1.8}
      />
      <span className="text-[12.5px] text-gray-500">{value}</span>
    </div>
  );
};

const ActiveStatusCell = ({ value }) => (
  <StatusBadge value={value ? 'ACTIVE' : 'INACTIVE'} />
);

const DateCell = ({ value }) => {
  if (!value) return <span className="text-[12.5px] text-gray-400">—</span>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()))
    return <span className="text-[12.5px] text-gray-400">—</span>;
  const formatted = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  return (
    <div className="flex items-center gap-1.5">
      <Calendar className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.8} />
      <span className="text-[12.5px] text-gray-500">{formatted}</span>
    </div>
  );
};

const createColumns = () => [
  {
    key: 'first_name',
    header: 'Name',
    minWidth: '200px',
    render: (_v, row) => <NameCell row={{ original: row }} />,
  },
  {
    key: 'email',
    header: 'Email',
    minWidth: '220px',
    render: (v) => <EmailCell value={v} />,
  },
  {
    key: 'phone',
    header: 'Phone',
    width: '170px',
    minWidth: '140px',
    render: (v) => <PhoneCell value={v} />,
  },
  {
    key: 'is_active',
    header: 'Status',
    width: '120px',
    minWidth: '110px',
    render: (v) => <ActiveStatusCell value={v} />,
  },
  {
    key: 'created_at',
    header: 'Created',
    width: '150px',
    minWidth: '130px',
    render: (v) => <DateCell value={v} />,
  },
];

const PartnerUsers = () => {
  const { orgId } = useParams();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );
  const [totalItems, setTotalItems] = useState(0);
  const [includeInactive, setIncludeInactive] = useState(
    searchParams.get('include_inactive') === 'true'
  );
  const [partnerName, setPartnerName] = useState('');
  const [partnerOrgId, setPartnerOrgId] = useState('');
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const addPartnerMemberMutation = useAddPartnerMember();
  const columns = createColumns();

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    if (params.include_inactive !== undefined)
      updatedParams.set('include_inactive', params.include_inactive.toString());
    setSearchParams(updatedParams);
  };

  const fetchPartnerMembers = async (page, size, includeInactiveFlag) => {
    if (!orgId) return;
    try {
      setIsLoading(true);
      const response = await PartnerService.getPartnerMembers({
        orgId,
        page,
        pageSize: size,
        includeInactive: includeInactiveFlag,
      });
      const usersData = response?.data?.users || response?.users || [];
      const pagination =
        response?.data?.pagination || response?.pagination || {};
      const totalCount =
        pagination.total ??
        pagination.total_items ??
        (Array.isArray(usersData) ? usersData.length : 0);
      setUsers(usersData);
      setTotalItems(totalCount);
    } catch (error) {
      console.error('Error fetching partner members:', error);
      toast({
        title: 'Error',
        description: 'Failed to load partner users. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchPartnerDetail = async () => {
      if (!orgId) return;
      try {
        const response = await PartnerService.getPartnerDetail(orgId);
        if (response?.name) setPartnerName(response.name);
        if (response?.org_id) setPartnerOrgId(response.org_id);
      } catch (error) {
        console.error('Error fetching partner detail:', error);
      }
    };
    fetchPartnerDetail();
  }, [orgId]);

  useEffect(() => {
    fetchPartnerMembers(currentPage, pageSize, includeInactive);
  }, [currentPage, pageSize, includeInactive, orgId]);

  useEffect(() => {
    if (!searchParams.has('pageSize')) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set('pageSize', DEFAULT_PAGE_SIZE.toString());
      setSearchParams(updatedParams);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page) => updateUrlParams({ page });
  const handlePageSizeChange = (newPageSize) =>
    updateUrlParams({ pageSize: newPageSize, page: 1 });

  const handleIncludeInactiveChange = (checked) => {
    setIncludeInactive(checked);
    updateUrlParams({ include_inactive: checked, page: 1 });
  };

  const handleSubmitUser = async (formData) => {
    if (!formData || !orgId) return;
    try {
      await addPartnerMemberMutation.mutateAsync({
        orgId,
        data: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          middleName: formData.middleName || '',
          email: formData.email,
          phone: formData.phone || '',
        },
      });
      toast({
        title: 'Success',
        description: 'Member added successfully!',
        variant: 'default',
      });
      setIsAddUserModalOpen(false);
      await fetchPartnerMembers(currentPage, pageSize, includeInactive);
    } catch (submitError) {
      console.error('Error adding user:', submitError);
      toast({
        title: 'Error',
        description:
          submitError.response?.data?.message ||
          'An error occurred while adding the member. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Partner Users', href: '/partner-users' },
    {
      label: partnerName || `Partner (${orgId})`,
      href: `/partner-users/${orgId}`,
    },
  ];

  const emptyState = (
    <div className="bg-card rounded-xl border border-gray-100 dark:border-border shadow-sm flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-5">
        <Users className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-tight">
        No users found
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs mb-5 leading-relaxed">
        {includeInactive
          ? 'No users found for this partner.'
          : 'No active users found. Toggle "Include inactive" to see all members.'}
      </p>
      <button
        type="button"
        onClick={() => setIsAddUserModalOpen(true)}
        className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer"
      >
        <UserRoundPlus className="w-3.5 h-3.5" strokeWidth={2} />
        Add Member
      </button>
    </div>
  );

  return (
    <div>
      <PageHeader
        breadcrumbs={breadcrumbItems}
        actions={
          <button
            type="button"
            onClick={() => setIsAddUserModalOpen(true)}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserRoundPlus className="w-3.5 h-3.5" strokeWidth={2} />
            Add Member
          </button>
        }
      />

      <PageContainer className="space-y-5">
        {partnerName && (
          <div className="flex items-center gap-3.5 px-4 py-3.5 bg-card border border-gray-100 dark:border-border rounded-[10px] shadow-sm">
            <div className="w-10 h-10 min-w-[40px] rounded-[10px] bg-brand-bg-subtle flex items-center justify-center text-[14px] font-bold text-[#2168B2] tracking-tight flex-shrink-0">
              {getInitials(partnerName)}
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-[14px] font-[650] text-foreground-strong leading-snug truncate text-left">
                {partnerName}
              </p>
              <p className="text-[12px] text-muted-foreground mt-0.5 text-left">
                {partnerOrgId || orgId}
              </p>
            </div>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-semibold tracking-wide uppercase bg-brand-bg-subtle text-[#2168B2] whitespace-nowrap flex-shrink-0">
              Partner
            </span>
          </div>
        )}

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={includeInactive}
              onCheckedChange={handleIncludeInactiveChange}
              disabled={isLoading}
            />
            <span className="text-[12.5px] font-medium text-gray-600">
              Include inactive
            </span>
          </label>
        </div>

        <DataTable
          className="rounded-xl overflow-hidden"
          data={users}
          columns={columns}
          loading={isLoading}
          tableLayout="auto"
          pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyState={emptyState}
          rowClassName={() =>
            'hover:bg-brand-bg-subtle transition-colors cursor-pointer'
          }
        />
      </PageContainer>

      <AddPartnerMemberModal
        isOpen={isAddUserModalOpen}
        onSubmit={handleSubmitUser}
        onClose={() => setIsAddUserModalOpen(false)}
        loading={addPartnerMemberMutation.isPending}
        partnerName={partnerName}
      />
    </div>
  );
};

export default PartnerUsers;
